import React, { useState, useEffect, useMemo, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import formattingUtils from '../../../Utils/formattingUtils';
import AdvancedAdjustmentPopover from './AdvancedAdjustmentPopoverBeta';
import { VossProductGroupTypeText } from '../../../Constants/VOSS/vossProductGroupType';
import { VossProductTypeText } from '../../../Constants/VOSS/vossProductType';
import './PriceSimulation.css';

// ─── Formatting Helpers ──────────────────────────────────────────────

const formatRevenue = (value) => new Intl.NumberFormat('sv-SE').format(Math.round(value));
const formatPrice = (value) => new Intl.NumberFormat('sv-SE').format(Math.round(value));
const formatDiscount = (value) =>
    new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(value);

const getStatusBadgeClass = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'approved') return 'plan-status-badge-approved';
    if (s === 'pendingreview') return 'plan-status-badge-pending';
    if (s === 'rejected') return 'plan-status-badge-rejected';
    return 'plan-status-badge-draft';
};

// ─── Component ───────────────────────────────────────────────────────

const PriceSimulationWorksheet = forwardRef(({ selectedPackages = [], modificationStatuses = [], selectedPlans = [], selectedProductTypes = [] }, ref) => {
    const { t } = useTranslation();

    // Adjustment values — stores percentage per plan: { [productId]: { [planId]: number } }
    const [adjustments, setAdjustments] = useState({});
    // Input mode — tracks % vs kr per plan: { [productId-planId]: 'percentage' | 'fixed' }
    const [inputModes, setInputModes] = useState({});
    // Fixed‑price cache — stores the kr value entered in fixed mode: { [productId-planId]: number }
    const [fixedValues, setFixedValues] = useState({});
    // Collapsible product groups: { [groupKey]: true (collapsed) }
    const [collapsedGroups, setCollapsedGroups] = useState({});
    // Collapsible tiers: { [tierKey]: true (collapsed) }
    const [collapsedTiers, setCollapsedTiers] = useState({});
    // Modal state for Edit Strategy popover
    const [modalState, setModalState] = useState({
        isOpen: false, product: null, plan: null, group: null, triggerRef: null, isBulkMode: false,
    });
    const triggerRefs = useRef({});
    // Temporary raw input values while user is typing: { [productId-planId]: string }
    const [editingInputs, setEditingInputs] = useState({});

    // Expose adjustments to parent via ref
    useImperativeHandle(ref, () => ({
        getAdjustments: () => adjustments,
        resetAdjustments: () => setAdjustments({}),
    }), [adjustments]);

    // ─── Seed adjustments from existing proposals ────────────────────

    useEffect(() => {
        if (!selectedPackages?.length) return;

        setAdjustments(prev => {
            const next = { ...prev };

            selectedPackages.forEach(pkg => {
                (pkg.tiers || []).forEach(tier => {
                    tier.productGroups.forEach(group => {
                        group.products.forEach(product => {
                            product.plans.forEach(plan => {
                                if (!next[product.id]?.[plan.id] && plan.adjustmentPercentage !== 0) {
                                    if (!next[product.id]) next[product.id] = {};
                                    next[product.id][plan.id] = {
                                        percentage: plan.adjustmentPercentage,
                                        newPrice: plan.newPrice,
                                    };
                                }
                            });
                        });
                    });
                });
            });

            return next;
        });
    }, [selectedPackages]);

    // ─── Multi‑Instance Map ──────────────────────────────────────────

    const productInstancesMap = useMemo(() => {
        if (!selectedPackages?.length) return {};
        const map = {};
        selectedPackages.forEach((pkg) => {
            (pkg.tiers || []).forEach((tier) => {
                tier.productGroups.forEach((group) => {
                    group.products.forEach((product) => {
                        if (selectedProductTypes.length > 0 &&
                            !selectedProductTypes.some(st => st.id === product.productType)) return;
                        const sku = product.productSku;
                        if (!map[sku]) map[sku] = [];
                        map[sku].push({
                            groupId: group.groupKey,
                            groupName: group.groupName,
                            productId: product.id,
                            productName: product.name,
                            productSku: sku,
                            plans: product.plans,
                        });
                    });
                });
            });
        });
        return map;
    }, [selectedPackages, selectedProductTypes]);

    const getRelatedInstances = useCallback(
        (sku, currentProductId) => (productInstancesMap[sku] || []).filter((i) => i.productId !== currentProductId),
        [productInstancesMap],
    );

    const getInstanceCount = useCallback(
        (sku) => (productInstancesMap[sku] || []).length,
        [productInstancesMap],
    );

    // ─── Adjustment Accessors ────────────────────────────────────────

    const getAdjustment = useCallback(
        (productId, planId) => adjustments[productId]?.[planId]?.percentage ?? 0,
        [adjustments],
    );

    const getInputMode = useCallback(
        (productId, planId) => inputModes[`${productId}-${planId}`] || 'percentage',
        [inputModes],
    );

    const getFixedValue = useCallback(
        (productId, planId) => fixedValues[`${productId}-${planId}`],
        [fixedValues],
    );

    // ─── Handlers ────────────────────────────────────────────────────

    const handleInputChange = useCallback((productId, planId, value) => {
        // Store raw string value during editing, allow "-", "", "5", "-5", etc.
        const key = `${productId}-${planId}`;
        setEditingInputs((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleInputBlur = useCallback((productId, planId, mode, currentPrice) => {
        const key = `${productId}-${planId}`;
        const rawValue = editingInputs[key];
        
        // Parse and sanitize on blur
        const numValue = rawValue === '' || rawValue === '-' || rawValue === '-.' || rawValue === undefined
            ? 0
            : parseFloat(rawValue) || 0;
        
        if (mode === 'fixed') {
            setFixedValues((prev) => ({ ...prev, [key]: numValue }));
            const pct = currentPrice ? ((numValue - currentPrice) / currentPrice) * 100 : 0;
            setAdjustments((prev) => ({
                ...prev,
                [productId]: { ...prev[productId], [planId]: { percentage: pct, newPrice: numValue } },
            }));
        } else {
            const newPrice = currentPrice * (1 + numValue / 100);
            setAdjustments((prev) => ({
                ...prev,
                [productId]: { ...prev[productId], [planId]: { percentage: numValue, newPrice } },
            }));
        }
        
        // Clear editing state
        setEditingInputs((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, [editingInputs]);

    const handleProductBulkAdjustment = useCallback((productId, plans, value) => {
        // Allow partial negative inputs like "-" or "-."
        const numValue = value === '' || value === '-' || value === '-.' ? 0 : parseFloat(value) || 0;
        setAdjustments((prev) => {
            const planAdj = {};
            plans.forEach((plan) => {
                const cp = plan.currentPrice || 0;
                planAdj[plan.id] = { percentage: numValue, newPrice: cp * (1 + numValue / 100) };
            });
            return { ...prev, [productId]: planAdj };
        });
    }, []);

    const toggleInputMode = useCallback((productId, planId, currentPrice) => {
        const key = `${productId}-${planId}`;
        
        // Clear any editing state
        setEditingInputs((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
        
        setInputModes((prev) => {
            const current = prev[key] || 'percentage';
            const next = current === 'percentage' ? 'fixed' : 'percentage';

            // When switching to fixed, prefer the stored newPrice (preserves user-entered
            // values across toggles, especially important when currentPrice is 0 since
            // the percentage encoding is lossy in that case)
            if (next === 'fixed') {
                const adjustmentData = adjustments[productId]?.[planId];
                const target = adjustmentData?.newPrice
                    ?? (currentPrice * (1 + (adjustmentData?.percentage ?? 0) / 100));
                setFixedValues((fp) => ({ ...fp, [key]: Math.round(target) }));
            }
            return { ...prev, [key]: next };
        });
    }, [adjustments]);

    const toggleGroup = useCallback((groupKey) => {
        setCollapsedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
    }, []);

    const toggleTier = useCallback((tierKey) => {
        setCollapsedTiers((prev) => ({ ...prev, [tierKey]: !prev[tierKey] }));
    }, []);

    const handleInputFocus = useCallback((e) => e.target.select(), []);

    // ─── Modal Handlers ──────────────────────────────────────────────

    const handleOpenModal = useCallback((product, plan, group, event, isBulkMode = false) => {
        let referencePlan = plan;
        if (isBulkMode) {
            referencePlan = product.plans.reduce(
                (shortest, c) => (c.months < shortest.months ? c : shortest),
                product.plans[0],
            );
        }
        const triggerKey = `${product.id}-${plan?.id || 'bulk'}`;
        const triggerElement = event?.currentTarget || triggerRefs.current[triggerKey];
        setModalState({ isOpen: true, product, plan: referencePlan, group, triggerRef: { current: triggerElement }, isBulkMode });
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalState({ isOpen: false, product: null, plan: null, group: null, triggerRef: null, isBulkMode: false });
    }, []);

    const handleModalApply = useCallback((adjustment, scope, isBulkMode) => {
        const { product } = modalState;
        if (!product) return;

        if (isBulkMode) {
            if (scope === 'allTiers') {
                const instances = productInstancesMap[product.productSku] || [];
                setAdjustments((prev) => {
                    const next = { ...prev };
                    instances.forEach((inst) => {
                        inst.plans.forEach((p) => {
                            if (!next[inst.productId]) next[inst.productId] = {};
                            const cp = p.currentPrice || 0;
                            next[inst.productId][p.id] = { percentage: adjustment, newPrice: cp * (1 + adjustment / 100) };
                        });
                    });
                    return next;
                });
            } else {
                handleProductBulkAdjustment(product.id, product.plans, adjustment);
            }
        } else {
            const { plan } = modalState;
            if (plan) {
                const cp = plan.currentPrice || 0;
                setAdjustments((prev) => ({
                    ...prev,
                    [product.id]: { ...prev[product.id], [plan.id]: { percentage: adjustment, newPrice: cp * (1 + adjustment / 100) } },
                }));
            }
        }
    }, [modalState, productInstancesMap, handleProductBulkAdjustment]);

    // ─── Calculations Memo ───────────────────────────────────────────

    // Helper to check if a plan matches the modification status filter
    const matchesStatusFilter = useCallback((plan, adjustment) => {
        // No filter selected = show all
        if (modificationStatuses.length === 0) return true;
        
        const statusIds = modificationStatuses.map(s => s.id);
        
        // 'All' filter = show everything
        if (statusIds.includes('All')) return true;
        
        const isModified = adjustment !== 0;
        const planStatus = plan.status?.toLowerCase() || 'draft';
        
        // OR logic: show if plan matches ANY selected status
        return statusIds.some(statusId => {
            switch (statusId) {
                case 'Draft':
                    return isModified;
                case 'Unmodified':
                    return !isModified;
                case 'PendingReview':
                    return planStatus === 'pendingreview';
                case 'Approved':
                    return planStatus === 'approved';
                case 'Rejected':
                    return planStatus === 'rejected';
                default:
                    return false;
            }
        });
    }, [modificationStatuses]);

    const calculations = useMemo(() => {
        if (!selectedPackages?.length) {
            return { packages: [], totalImpact: 0, averageAdjustment: 0, planCount: 0 };
        }

        let totalImpact = 0, totalAdjSum = 0, planCount = 0;

        const packages = selectedPackages.map((pkg) => {
            const tiers = (pkg.tiers || []).map((tier) => {
                const productGroups = tier.productGroups
                    .map((group) => {
                        let groupOriginalTotal = 0, groupNewTotal = 0;

                        const products = group.products
                            .filter((product) => {
                                if (selectedProductTypes.length === 0) return true;
                                return selectedProductTypes.some(st => st.id === product.productType);
                            })
                            .map((product) => {
                            // First compute adjustment for each plan, then filter
                            const allPlans = product.plans.map((plan) => {
                                const isTieredPricing = (plan.pricingScheme ?? 0) === 1;

                                if (isTieredPricing) {
                                    return {
                                        ...plan,
                                        pricingScheme: 1,
                                        currentPrice: null,
                                        adjustment: 0,
                                        newListPrice: null,
                                        delta: 0,
                                    };
                                }

                                // Flat pricing — price comes directly from transformer
                                const currentPrice = plan.currentPrice || 0;
                                const adjustmentData = adjustments[product.id]?.[plan.id];
                                const adjustment = adjustmentData?.percentage ?? 0;
                                const newListPrice = adjustmentData?.newPrice ?? (currentPrice * (1 + adjustment / 100));
                                const delta = newListPrice - currentPrice;
                                return { ...plan, pricingScheme: 0, currentPrice, adjustment, newListPrice, delta };
                            });
                            
                            // Filter plans based on modification status and plan selection
                            const filteredPlans = allPlans.filter((plan) => {
                                if (selectedPlans.length > 0 && !selectedPlans.some(sp => sp.id === plan.identifier)) return false;
                                return matchesStatusFilter(plan, plan.adjustment);
                            });
                            
                            // Only count filtered plans in totals (skip tiered-pricing plans)
                            filteredPlans.forEach((plan) => {
                                if ((plan.pricingScheme ?? 0) === 1) return;
                                groupOriginalTotal += plan.currentPrice;
                                groupNewTotal += plan.newListPrice;
                                totalImpact += plan.delta;
                                totalAdjSum += plan.adjustment;
                                planCount++;
                            });
                            
                            return {
                                ...product,
                                plans: filteredPlans,
                            };
                        }).filter((product) => product.plans.length > 0); // Remove products with no matching plans

                        return { ...group, products, groupOriginalTotal, groupNewTotal };
                    })
                    .filter((group) => group.products.length > 0) // Remove groups with no matching products
                    .sort((a, b) => (b.isIncluded ? 1 : 0) - (a.isIncluded ? 1 : 0));

                // Compute tier-level totals
                let tierOriginalTotal = 0, tierNewTotal = 0;
                productGroups.forEach((g) => {
                    tierOriginalTotal += g.groupOriginalTotal;
                    tierNewTotal += g.groupNewTotal;
                });

                return { ...tier, productGroups, tierOriginalTotal, tierNewTotal };
            }).filter((tier) => tier.productGroups.length > 0); // Remove tiers with no matching groups

            return { packageId: pkg.packageId, packageName: pkg.packageName, tiers };
        }).filter((pkg) => pkg.tiers.length > 0); // Remove packages with no matching tiers

        return {
            packages,
            totalImpact,
            averageAdjustment: planCount > 0 ? totalAdjSum / planCount : 0,
            planCount,
        };
    }, [selectedPackages, getAdjustment, matchesStatusFilter, selectedPlans, selectedProductTypes]);

    // ─── Render: Empty State ─────────────────────────────────────────

    if (!selectedPackages?.length) {
        return (
            <div className="price-adjustment-worksheet">
                <div className="worksheet-empty-state">
                    <p>{t('priceSimulation.selectPackagePrompt')}</p>
                </div>
            </div>
        );
    }

    // ─── Render ──────────────────────────────────────────────────────

    return (
        <div className="price-adjustment-worksheet">
            {/* Summary Cards */}
            <div className="worksheet-summary">
                <div className="summary-card">
                    <span className="summary-label">{t('priceSimulation.totalImpact')}</span>
                    <span className="summary-value impact-value">{formattingUtils.currencyFormatter(calculations.totalImpact)}</span>
                </div>
                <div className="summary-card">
                    <span className="summary-label">{t('priceSimulation.averageIncrease')}</span>
                    <span className="summary-value average-value">{calculations.averageAdjustment.toFixed(2)}%</span>
                </div>
                <div className="summary-card">
                    <span className="summary-label">{t('priceSimulation.plansAffected')}</span>
                    <span className="summary-value">{calculations.planCount}</span>
                </div>
            </div>

            {/* Tier Containers */}
            <div className="worksheet-tiers-container">
                {calculations.packages.map((pkg) => (
                    <React.Fragment key={pkg.packageId}>
                    {pkg.tiers.map((tier) => {
                        const isTierCollapsed = collapsedTiers[tier.tierKey] || false;
                        const tierDiff = tier.tierNewTotal - tier.tierOriginalTotal;
                        const tierPctChange = tier.tierOriginalTotal
                            ? ((tierDiff / tier.tierOriginalTotal) * 100).toFixed(1)
                            : '0.0';

                        return (
                        <div key={tier.tierKey} className={`worksheet-tier-card${tier.isOutdatedVersion ? ' tier-outdated' : ''}`}>
                            {/* Tier Header — white card, clickable */}
                            <div
                                className="tier-card-header"
                                onClick={() => toggleTier(tier.tierKey)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleTier(tier.tierKey); }}
                            >
                                <div className="tier-header-left">
                                    <span className={`expand-chevron ${isTierCollapsed ? '' : 'expanded'}`}>
                                        <span className="caret caret-lg"></span>
                                    </span>
                                    <span className="tier-card-title">
                                        {pkg.packageName} &mdash; {tier.tierName}
                                    </span>
                                {tier.isOutdatedVersion && (
                                    <span className="tier-outdated-badge">
                                        <span className="vismaicon vismaicon-sm vismaicon-warning"></span>
                                        {t('priceSimulation.outdatedSourceData')}
                                    </span>
                                )}
                            </div>
                            <div className="tier-header-right">
                                <span className="tier-total">
                                    {t('priceSimulation.tierTotal')}: {formatRevenue(tier.tierOriginalTotal)} SEK
                                    {tierDiff !== 0 && (
                                        <>
                                            {' → '}
                                            <span className="tier-total-new">{formatRevenue(tier.tierNewTotal)} SEK</span>
                                            <span className="tier-total-delta">
                                                {' '}({tierDiff > 0 ? '+' : ''}{formatRevenue(tierDiff)} / {tierDiff > 0 ? '+' : ''}{tierPctChange}%)
                                            </span>
                                        </>
                                    )}
                                </span>
                            </div>
                        </div>

                        {/* Collapsible tier content */}
                        {!isTierCollapsed && (
                        <table className="table table-stripe skynet-table-no-divider worksheet-table">
                            <colgroup>
                                <col className="col-product-plan" />
                                <col className="col-current-price" />
                                <col className="col-adjustment" />
                                <col className="col-new-price" />
                            </colgroup>
                            <thead>
                                <tr style={{ height: '5.6rem' }}>
                                    <th className="col-product-plan">{t('priceSimulation.productPlan')}</th>
                                    <th className="col-current-price">{t('priceSimulation.currentPrice')} (SEK)</th>
                                    <th className="col-adjustment">{t('priceSimulation.adjustment')}</th>
                                    <th className="col-new-price">{t('priceSimulation.newPrice')} (SEK)</th>
                                </tr>
                            </thead>

                            {tier.productGroups.map((group) => {
                                const isCollapsed = collapsedGroups[group.groupKey] || false;
                                const groupDiff = group.groupNewTotal - group.groupOriginalTotal;
                                const groupPctChange = group.groupOriginalTotal
                                    ? ((groupDiff / group.groupOriginalTotal) * 100).toFixed(1)
                                    : '0.0';

                                return (
                                    <tbody key={group.groupKey}>
                                        {/* Group Divider Row */}
                                        <tr
                                            className="worksheet-group-divider-row"
                                            onClick={() => toggleGroup(group.groupKey)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleGroup(group.groupKey); }}
                                        >
                                            <td colSpan={4}>
                                                <div className="group-divider-content">
                                                    <div className="group-divider-left">
                                                        <span className="group-name">{group.groupName}</span>
                                                        <span className={`tag-badge tag-${group.isIncluded ? 'included' : 'optional'}`}>
                                                            {t(VossProductGroupTypeText[group.productGroupType] || 'vossProductGroupType.included')}
                                                        </span>
                                                    </div>
                                                    <div className="group-divider-right">
                                                        <span className="product-group-total">
                                                            {t('priceSimulation.groupTotal')}: {formatRevenue(group.groupOriginalTotal)} SEK
                                                            {groupDiff !== 0 && (
                                                                <>
                                                                    {' → '}
                                                                    <span className="group-total-new">{formatRevenue(group.groupNewTotal)} SEK</span>
                                                                    <span className="group-total-diff">
                                                                        {' '}({groupDiff > 0 ? '+' : ''}{formatRevenue(groupDiff)} / {groupDiff > 0 ? '+' : ''}{groupPctChange}%)
                                                                    </span>
                                                                </>
                                                            )}
                                                        </span>
                                                        <span className={`expand-chevron ${isCollapsed ? '' : 'expanded'}`}>
                                                            <span className="caret caret-lg"></span>
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>

                                        {/* Product & Plan rows (collapsible) */}
                                        {!isCollapsed && group.products.map((product) => (
                                            <React.Fragment key={product.id}>
                                                {/* Product Parent Row */}
                                                <tr className="worksheet-product-row">
                                                    <td className="col-product-plan">
                                                        <div className="product-row-content">
                                                            <span className="vismaicon vismaicon-sm vismaicon-expense-1"></span>
                                                            <div className="product-name-wrapper">
                                                                <span className="product-name">{product.name}
                                                                {getInstanceCount(product.productSku) > 1 && (
                                                                <svg className="multi-instance-icon ml-8" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                                                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                                                    <title>{t('priceSimulation.productExistsInTiers', { count: getInstanceCount(product.productSku) })}</title>
                                                                </svg>
                                                            )}
                                                                </span>
                                                                {product.productSku && (
                                                                    <span className="product-article-number">
                                                                        {product.productSku}
                                                                        {product.productType !== undefined && ` • ${t(VossProductTypeText[product.productType])}`}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="col-current-price"></td>
                                                    <td className="col-adjustment">
                                                        <button
                                                            type="button"
                                                            ref={(el) => {
                                                                const key = `${product.id}-bulk`;
                                                                if (el) triggerRefs.current[key] = el;
                                                            }}
                                                            className="btn btn-ghost edit-strategy-btn"
                                                            onClick={(e) => handleOpenModal(product, null, { ...group, name: group.groupName }, e, true)}
                                                        >
                                                            <span className="vismaicon vismaicon-sm vismaicon-multiple-selection"></span>
                                                            {t('priceSimulation.adjustAllPlans')}
                                                        </button>
                                                    </td>
                                                    <td className="col-new-price"></td>
                                                </tr>

                                                {/* Plan Child Rows */}
                                                {product.plans.map((plan) => {
                                                    const mode = getInputMode(product.id, plan.id);
                                                    const isFixed = mode === 'fixed';
                                                    const key = `${product.id}-${plan.id}`;
                                                    const displayValue = editingInputs[key] !== undefined
                                                        ? editingInputs[key]
                                                        : isFixed
                                                            ? (getFixedValue(product.id, plan.id) ?? Math.round(plan.newListPrice))
                                                            : plan.adjustment;
                                                    const isZeroValue = displayValue === 0 || displayValue === '0' || (!isFixed && plan.adjustment === 0);
                                                    const isLocked = plan.status != null && plan.status.toLowerCase() !== 'draft';

                                                    return (
                                                        <tr key={plan.id} className="worksheet-plan-row">
                                                            <td className="col-product-plan">
                                                                <div className="plan-row-content">
                                                                    <svg className="plan-tree-icon" width="12" height="12" viewBox="0 0 12 12">
                                                                        <path d="M 2 0 L 2 8 L 10 8" stroke="#cbd5e0" strokeWidth="1.5" fill="none" />
                                                                    </svg>
                                                                    <div className="plan-info">
                                                                        <div className="plan-name-row">
                                                                            <span className="plan-name">{plan.name}</span>
                                                                            {plan.status?.toLowerCase() !== 'draft' && plan.status != null ? (
                                                                                <span className={`plan-status-badge ${getStatusBadgeClass(plan.status)}`}>{plan.status}</span>
                                                                            ) : plan.adjustment !== 0 ? (
                                                                                <span className="plan-status-badge plan-status-badge-modified">{t('priceSimulation.modified')}</span>
                                                                            ) : null}
                                                                        </div>
                                                                        <div className="plan-metadata">
                                                                            <span>Rev: {formatRevenue(plan.revenue || 0)} SEK</span>
                                                                            <span className="metadata-separator">•</span>
                                                                            <span>Disc: {formatDiscount(plan.averageDiscount || 0)}%</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="col-current-price">
                                                                {(plan.pricingScheme ?? 0) === 1
                                                                    ? <span className="volume-pricing-label">{t('priceSimulation.volumePricing')}</span>
                                                                    : <>{formatPrice(plan.currentPrice)} SEK</>
                                                                }
                                                            </td>
                                                            <td className="col-adjustment">
                                                                {(plan.pricingScheme ?? 0) === 1 ? (
                                                                    <span className="volume-pricing-label">
                                                                        {t('priceSimulation.tieredPricingComingSoon')}
                                                                    </span>
                                                                ) : (
                                                                    <div className="adjustment-input-wrapper">
                                                                        <input
                                                                            type="number"
                                                                            className={`form-control adjustment-input${isZeroValue ? ' input-zero' : ''}`}
                                                                            value={displayValue}
                                                                            onChange={(e) => handleInputChange(product.id, plan.id, e.target.value)}
                                                                            onBlur={() => handleInputBlur(product.id, plan.id, mode, plan.currentPrice)}
                                                                            onFocus={handleInputFocus}
                                                                            step={isFixed ? '1' : '0.1'}
                                                                            placeholder={isFixed ? formatPrice(plan.currentPrice) : '0'}
                                                                            disabled={isLocked}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            className="unit-toggle-btn"
                                                                            onClick={() => toggleInputMode(product.id, plan.id, plan.currentPrice)}
                                                                            title={isFixed ? t('priceSimulation.switchToPercentage') : t('priceSimulation.switchToFixed')}
                                                                            disabled={isLocked}
                                                                        >
                                                                            {isFixed ? 'kr' : '%'}
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="col-new-price">
                                                                {(plan.pricingScheme ?? 0) === 1 ? (
                                                                    <span className="volume-pricing-label">&mdash;</span>
                                                                ) : (
                                                                    <div className="price-with-delta">
                                                                        <span className={`new-price-value ${plan.delta !== 0 ? 'price-changed' : 'price-unchanged'}`}>
                                                                            {formatPrice(plan.newListPrice)} SEK
                                                                        </span>
                                                                        {plan.delta !== 0 && (
                                                                            <span className={`delta-value ${plan.delta > 0 ? 'delta-positive' : 'delta-negative'}`}>
                                                                                {plan.delta > 0 ? '+' : ''}{formatPrice(plan.delta)} SEK
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                );
                            })}
                        </table>
                        )}
                    </div>
                    );
                })}
                    </React.Fragment>
                ))}
            </div> 

            {/* Edit Strategy Popover (kept for bulk mode) */}
            <AdvancedAdjustmentPopover
                isOpen={modalState.isOpen}
                onClose={handleCloseModal}
                onApply={handleModalApply}
                product={modalState.product}
                plan={modalState.plan}
                group={modalState.group}
                triggerRef={modalState.triggerRef}
                isBulkMode={modalState.isBulkMode}
                relatedInstances={
                    modalState.product
                        ? getRelatedInstances(modalState.product.productSku, modalState.product.id).map((inst) => ({
                            groupName: inst.groupName,
                            productName: inst.productName,
                            currentPrice: inst.plans.find((p) => p.name === modalState.plan?.name)?.currentPrice || 0,
                        }))
                        : []
                }
                currentAdjustment={
                    modalState.product && modalState.plan
                        ? getAdjustment(modalState.product.id, modalState.plan.id)
                        : 0
                }
            />
        </div>
    );
});

export default PriceSimulationWorksheet;
